import React, { useState } from "react";
import { Card } from "../../../../shared/Card/Card";
import { PrimaryButton } from "../../../../shared/Buttons/Buttons";
import "./tabs.scss";

interface QuestionAnalysisTabProps {
  evaluations: any[];
  interview: any;
}

interface QuestionItem {
  id: string;
  question: string;
  answer?: string;
  technicalScore?: number;
  communicationScore?: number;
  maxScore?: number;
  strengths?: string[];
  areasForImprovement?: string[];
  whatCanBeDoneBetter?: string[];
  overallFeedback?: string;
  weight?: number;
}

const QuestionAnalysisTab: React.FC<QuestionAnalysisTabProps> = ({
  evaluations,
  interview,
}) => {
  const [selectedQuestionIndex, setSelectedQuestionIndex] = useState(0);

  // Transform evaluations data into question items
  const questions: QuestionItem[] = evaluations.map(
    (evaluation: any, index: number) => ({
      id: evaluation.id || `question-${index}`,
      question:
        evaluation.question ||
        evaluation.questionText ||
        `Question ${index + 1}`,
      answer: evaluation.answer || evaluation.userAnswer || evaluation.response,
      technicalScore:
        evaluation.technicalScore || evaluation.technical_score || 0,
      communicationScore:
        evaluation.communicationScore || evaluation.communication_score || 0,
      maxScore: evaluation.maxScore || 10,
      strengths: evaluation.strengths || evaluation.feedback?.strengths || [],
      areasForImprovement:
        evaluation.areasForImprovement ||
        evaluation.feedback?.areasForImprovement ||
        [],
      whatCanBeDoneBetter:
        evaluation.whatCanBeDoneBetter ||
        evaluation.feedback?.whatCanBeDoneBetter ||
        [],
      overallFeedback:
        evaluation.overallFeedback ||
        evaluation.feedback?.overall ||
        evaluation.feedback,
      weight: evaluation.weight || evaluation.percentage || 0,
    })
  );

  const selectedQuestion = questions[selectedQuestionIndex];

  const handlePrevious = () => {
    if (selectedQuestionIndex > 0) {
      setSelectedQuestionIndex(selectedQuestionIndex - 1);
    }
  };

  const handleNext = () => {
    if (selectedQuestionIndex < questions.length - 1) {
      setSelectedQuestionIndex(selectedQuestionIndex + 1);
    }
  };

  const getScoreColor = (score: number, maxScore: number = 10): string => {
    const percentage = (score / maxScore) * 100;
    if (percentage >= 70) return "#10b981";
    if (percentage >= 50) return "#f59e0b";
    return "#ef4444";
  };

  const getBorderColor = (index: number): string => {
    if (index === selectedQuestionIndex) return "#2563eb"; // Blue for selected
    if (index < selectedQuestionIndex) return "#f59e0b"; // Orange for completed
    return "#e5e7eb"; // Gray for others
  };

  if (!evaluations || evaluations.length === 0) {
    return (
      <div className="question-analysis-tab">
        <Card>
          <div className="tab-content-wrapper">
            <div className="no-data-message">
              No evaluations available for this interview.
            </div>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="question-analysis-tab">
      <div className="question-analysis-content">
        {/* Left Panel - Questions List */}
        <Card>
          <div className="questions-sidebar">
            <h3 className="sidebar-title">Questions</h3>
            <div className="questions-list">
              {questions.map((question, index) => (
                <div
                  key={question.id}
                  className={`question-item ${
                    index === selectedQuestionIndex ? "selected" : ""
                  }`}
                  style={{
                    borderLeft: `4px solid ${getBorderColor(index)}`,
                  }}
                  onClick={() => setSelectedQuestionIndex(index)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      setSelectedQuestionIndex(index);
                    }
                  }}
                >
                  <div className="question-text">{question.question}</div>
                  {/* {question.weight && (
                    <div className="question-weight">{question.weight}%</div>
                  )} */}
                </div>
              ))}
            </div>
          </div>
        </Card>

        {/* Right Panel - Question Details */}
        <Card>
          <div className="question-details">
            {selectedQuestion ? (
              <>
                <h3 className="question-title">{selectedQuestion.question}</h3>

                {/* Your Answer Section */}
                <div className="answer-section">
                  <div className="section-label">Your Answer</div>
                  <div className="answer-box">
                    {selectedQuestion.answer || "No answer provided"}
                  </div>
                </div>

                {/* Scores Section */}
                <div className="scores-section">
                  <div className="score-card">
                    <div className="score-label">Technical Score</div>
                    <div
                      className="score-value"
                      style={{
                        color: getScoreColor(
                          selectedQuestion.technicalScore || 0,
                          selectedQuestion.maxScore || 10
                        ),
                      }}
                    >
                      {selectedQuestion.technicalScore || 0}/
                      {selectedQuestion.maxScore || 10}
                    </div>
                  </div>
                  <div className="score-card">
                    <div className="score-label">Communication Score</div>
                    <div
                      className="score-value"
                      style={{
                        color: getScoreColor(
                          selectedQuestion.communicationScore || 0,
                          selectedQuestion.maxScore || 10
                        ),
                      }}
                    >
                      {selectedQuestion.communicationScore || 0}/
                      {selectedQuestion.maxScore || 10}
                    </div>
                  </div>
                </div>

                {/* Feedback Sections */}
                <div className="feedback-sections">
                  {/* Strengths */}
                  {selectedQuestion.strengths &&
                    selectedQuestion.strengths.length > 0 && (
                      <div className="feedback-box strengths">
                        <div className="feedback-box-header">
                          <span className="feedback-icon">✓</span>
                          <h4 className="feedback-title">Strengths</h4>
                        </div>
                        <ul className="feedback-list">
                          {selectedQuestion.strengths.map(
                            (strength: string, index: number) => {
                              const uniqueKey = `strength-${strength.substring(
                                0,
                                30
                              )}-${strength.length}-${index}`;
                              return <li key={uniqueKey}>{strength}</li>;
                            }
                          )}
                        </ul>
                      </div>
                    )}

                  {/* Areas for Improvement */}
                  {selectedQuestion.areasForImprovement &&
                    selectedQuestion.areasForImprovement.length > 0 && (
                      <div className="feedback-box improvement">
                        <div className="feedback-box-header">
                          <span className="feedback-icon">!</span>
                          <h4 className="feedback-title">
                            Areas for Improvement
                          </h4>
                        </div>
                        <ul className="feedback-list">
                          {selectedQuestion.areasForImprovement.map(
                            (area: string, index: number) => {
                              const uniqueKey = `improvement-${area.substring(
                                0,
                                30
                              )}-${area.length}-${index}`;
                              return <li key={uniqueKey}>{area}</li>;
                            }
                          )}
                        </ul>
                      </div>
                    )}

                  {/* What Can Be Done Better */}
                  {selectedQuestion.whatCanBeDoneBetter &&
                    selectedQuestion.whatCanBeDoneBetter.length > 0 && (
                      <div className="feedback-box better">
                        <div className="feedback-box-header">
                          <span className="feedback-icon">→</span>
                          <h4 className="feedback-title">
                            What Can Be Done Better
                          </h4>
                        </div>
                        <ul className="feedback-list">
                          {selectedQuestion.whatCanBeDoneBetter.map(
                            (item: string, index: number) => {
                              const uniqueKey = `better-${item.substring(
                                0,
                                30
                              )}-${item.length}-${index}`;
                              return <li key={uniqueKey}>{item}</li>;
                            }
                          )}
                        </ul>
                      </div>
                    )}
                </div>

                {/* Overall Feedback */}
                {selectedQuestion.overallFeedback && (
                  <div className="overall-feedback-section">
                    <div className="section-label">Overall Feedback</div>
                    <div className="overall-feedback-box">
                      {selectedQuestion.overallFeedback}
                    </div>
                  </div>
                )}

                {/* Navigation Buttons */}
                <div className="question-navigation">
                  <PrimaryButton
                    text="Previous Question"
                    type="button"
                    onClick={handlePrevious}
                    disable={selectedQuestionIndex === 0}
                    size="sm"
                  />
                  <PrimaryButton
                    text="Next Question"
                    type="button"
                    onClick={handleNext}
                    disable={selectedQuestionIndex === questions.length - 1}
                    size="sm"
                  />
                </div>
              </>
            ) : (
              <div className="no-question-selected">
                Please select a question to view its analysis.
              </div>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
};

export default QuestionAnalysisTab;
